import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import CheckBox from 'apollo-react/components/Checkbox';
import Grid from 'apollo-react/components/Grid';
import Cog from 'apollo-react-icons/Cog';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import BackButton from '../../components/buttons/BackButton';
import HelpLink from '../../components/HelpLink';
import LazyText from '../../components/LazyText';
import * as Urls from '../../constants/Urls';
import { FORM_EDITOR } from '../../constants/Urls';
import { useFormSelector } from '../../custom-hooks';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { getPagePathname } from '../../utils/childrenWindowsUtils';
import { getUITA } from '../../utils/commonUtils';
import { handleFormChangesMode } from '../actions/runtime.actions';
import { FORM_KEYS } from '../constants/form-manager.constants';
import {
  getFormInfo,
  getIsFormChangesMode,
  getIsFormChangesTableLoading,
  getUserContext,
} from '../selectors/common.selectors';
import { getFormHasChanges } from '../selectors/form-manager.selectors';

const useStyles = makeStyles({
  wrapper: {
    borderBottom: '1px solid #e9e9e9',
    padding: '20px 0px 20px 32px',
    '& ul': {
      marginLeft: '15px',
    },
  },
  title: {
    fontFamily: 'Proxima Nova',
    fontSize: 20,
    fontWeight: 600,
    fontStyle: 'normal',
    fontStretch: 'normal',
    lineHeight: 2,
    letterSpacing: 'normal',
    paddingTop: 2,
    color: 'var(--black)',
  },
  uiChanges: {
    color: 'orange',
  },
  deltaChanges: {
    color: 'blue',
  },
  addedChanges: {
    color: 'green',
  },
  titleWrapper: {
    alignItems: 'center',
    display: 'flex',
    gap: 5,
  },
  buttonsWrapper: {
    display: 'flex',
    gap: 10,
    textAlign: 'right',
  },
  marginRight: {
    marginRight: 15,
  },

  showFormChangesContainer: {
    display: 'flex',
    justifyContent: 'center',
    minWidth: 200,
    '& label': {
      marginRight: 0,
    },
  },

  editModeButton: {
    minWidth: 140,
  },
});

const FormHeader = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const translateString = useTranslation();
  const history = useHistory();
  const userContext = useFormSelector(getUserContext);
  const { formKey } = useFormSelector(getFormInfo);
  const isFormChangesMode = useFormSelector(getIsFormChangesMode);
  const formHasChanges = useFormSelector(getFormHasChanges);
  const isFormChangesTableLoading = useFormSelector(getIsFormChangesTableLoading);

  const { search } = document.location;
  const hasFormsDesignPermission = userContext?.f_CurrentRights?.FORMS_DESIGN;
  const showBackButton = [Urls.TRAININGS].includes(getPagePathname());
  const formName = translateString(formKey);

  useEffect(() => {
    document.title = formName || 'Initiate';
  }, [formName]);

  const openFormEditorPage = (pageDetails, search) => {
    history.push(pageDetails + search);
  };

  return (
    <div className={classes.wrapper}>
      <Grid container>
        <Grid className={classes.titleWrapper} item xs={6}>
          <LazyText text={formName}>
            <span className={classes.title} ui-ta={getUITA('CreateHeader')}>
              {formName}
            </span>
            <HelpLink />
          </LazyText>
        </Grid>
        <Grid item xs={6} justifyContent="flex-end" className={classes.buttonsWrapper}>
          {showBackButton && <BackButton isFormPage />}
          {hasFormsDesignPermission && (
            <div className={classes.showFormChangesContainer}>
              <CheckBox
                label={translateString('Show Form Delta Changes')}
                disabled={!formHasChanges || isFormChangesTableLoading}
                checked={isFormChangesMode}
                onChange={() => dispatch(handleFormChangesMode(!isFormChangesMode, FORM_KEYS.STEP))}
                ui-ta={getUITA('ShowFormChanges')}
              />
            </div>
          )}

          {hasFormsDesignPermission && (
            <Button
              icon={Cog}
              onClick={() => openFormEditorPage(FORM_EDITOR, search)}
              className={classes.editModeButton}
            >
              {translateString('Edit Mode')}
            </Button>
          )}
        </Grid>
      </Grid>
    </div>
  );
};

export default FormHeader;
